import { useLocale } from '../i18n/LocaleContext';
import { Link } from 'react-router-dom';
import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Hero = () => {
  const { t, appLink, path } = useLocale();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [showHint, setShowHint] = useState(true);
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted(!muted);
      setShowHint(true); // briefly show hint again on toggle
      setTimeout(() => setShowHint(false), 2500);
    }
  };

  useEffect(() => {
    // Auto-hide hint after 3 seconds initially
    const timer = setTimeout(() => setShowHint(false), 3000);
    return () => clearTimeout(timer);
  }, []);
  // in Hero.jsx
  const handleSignupClick = () => {
    const send = (...args) => {
      if (typeof window.gtag === 'function') {
        window.gtag(...args);
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(args);
      } else {
        // Optional: debug while developing
        console.warn('gtag/dataLayer not ready; queued event:', args);
      }
    };

    // GA4 event (shows up in GA4 > DebugView + Realtime)
    send('event', 'sign_up', {
      method: 'Hero CTA',
      link_url: appLink(),
    });

    // Google Ads conversion (your existing action)
    send('event', 'conversion', {
      send_to: 'AW-17463863166/8FliCLaN94gbEP7OtYdB',
      // value/currency optional; add if your conversion action expects it
      // value: 0, currency: 'USD',
    });
  };

  return (
    <HeroSection>
      <HeroContainer>
        <HeroContent>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Kicker>Noteician · {t('hero.audience')}</Kicker>
            <HeroHeading>{t('hero.heading')}</HeroHeading>
            <HeroSubtitle>
              {t('hero.description')}
            </HeroSubtitle>
            <TrialMessage>
              {t('hero.trial')}
            </TrialMessage>
            <HeroButtons>
                <PrimaryButton
                  href={appLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -5 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSignupClick}
                >
                  {t('hero.startTrial')}
                </PrimaryButton>

                <SecondaryButton
                  to={path('/how-to-use')}
                >
                  {t('hero.seeWorkflow')}
                </SecondaryButton>

                <HelperText>{t('hero.noCard')}</HelperText>
            </HeroButtons>
          </motion.div>
        </HeroContent>
        <HeroImageContainer>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <HeroVideo
              ref={videoRef}
              src={process.env.PUBLIC_URL + '/noteician_web_video.mp4'}
              autoPlay
              loop
              muted={muted}
              playsInline
              onClick={toggleMute}
            />

            <VideoHint
              as={motion.div}
              initial={{ opacity: 1 }}
              animate={{ opacity: showHint ? 1 : 0 }}
              transition={{ duration: 0.6 }}
            >
              {muted ? t('video.unmute') : t('video.mute')}
            </VideoHint>
          </motion.div>
        </HeroImageContainer>

      </HeroContainer>
    </HeroSection>
  );
};

const HeroSection = styled.section`
  padding: 100px 0;
  background-color: var(--white);
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const HeroContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  flex: 0 0 55%;

  @media (max-width: 992px) {
    flex: 0 0 100%;
    width: 100%;
    min-width: 0;
    margin-bottom: 40px;
  }
`;

const HeroHeading = styled.h1`
  font-size: clamp(2.2rem, 4vw, 3.25rem);
  font-weight: 700;
  margin-bottom: 20px;
  color: var(--dark-gray);

`;

const Kicker = styled.p`
  color: var(--primary-dark);
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.6;
  margin-bottom: 16px;
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  margin-bottom: 30px;
  color: var(--secondary);
  max-width: 500px;

  @media (max-width: 992px) {
    margin: 0 auto 30px;
  }
`;
const HeroButtons = styled.div`
  display: inline-grid;
  grid-template-columns: 1fr 1fr;      /* two equal tracks */
  grid-template-areas:
    "primary  secondary"
    "helper   .";
  gap: 8px 15px;
  align-items: start;                  /* avoid vertical stretching */

  @media (max-width: 576px) {
    display: grid;
    width: 100%;
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: "primary" "helper" "secondary";
  }
`;

const HelperText = styled.span`
  grid-area: helper;
  font-size: 0.9rem;
  line-height: 1.2;
  color: var(--secondary);
  opacity: 0.85;
  justify-self: start;
`;
const PrimaryButton = styled(motion.a)`
  grid-area: primary;
  width: 100%;
  display: inline-block;
  padding: 15px 30px;
  min-height: 54px;                  /* keep both buttons the same height */
  box-sizing: border-box;            /* include border in total height */
  border: 2px solid transparent;     /* match secondary’s border thickness */
  background: var(--gradient);
  color: white;
  border-radius: 8px;
  font-weight: 600;
  box-shadow: var(--shadow);
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
`;

const SecondaryButton = styled(Link)`
  grid-area: secondary;
  width: 100%;
  display: inline-block;
  padding: 15px 30px;
  min-height: 54px;                  /* same as primary */
  box-sizing: border-box;            /* include border in total height */
  background: white;
  color: var(--primary);
  border: 2px solid var(--primary);
  border-radius: 8px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  align-self: start;
`;

const HeroImageContainer = styled.div`
  flex: 0 0 40%;

  @media (max-width: 992px) {
    flex: 0 0 100%;
  }
`;

const TrialMessage = styled.div`
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 30px;
  color: var(--dark-gray);

  span {
    color: var(--primary);
    font-weight: 700;
  }

  @media (max-width: 992px) {
    margin: 0 auto 30px;
    text-align: center;
  }
`;


const HeroVideo = styled.video`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 20px;
  margin: 0 auto;
  cursor: pointer;
  box-shadow: var(--shadow);

  /* bleed a little wider only on larger screens */
  @media (min-width: 992px) {
    width: 110%;
    transform: translateX(-5%); /* re-center the extra width */
  }
`;


const VideoHint = styled.div`
  text-align: center;
  margin-top: 10px;
  font-size: 0.9rem;
  color: var(--secondary);
  opacity: 0.85;
  min-height: 1.2em; /* always reserve space for text line */
`;


export default Hero;
